<template>
<span v-if="!isPopupOpen || !inModal">
	<Form
		ref="form"
		@submit="onSubmit"
		@invalid-submit="console.error"
		:validation-schema="schema"
		:initial-values="formValues"
		v-slot="{ errors, meta }"
	>
	<fieldset :disabled="!isEnabled">
		<legend
			v-if="!inModal"
			class="row form-row g-2 p-0 pb-2 m-0"
		>
			<div class="text-center text-nowrap">Годинник сервісного модуля</div>
		</legend>

		<div class="row form-row g-0 gx-2 p-0 px-2 m-0">
			<div class="form-group col-auto my-0">
				<label>Установка часу</label>
				<Field
					:name="clockFieldKey"
					type="datetime-local"
					class="form-control form-control-sm no-arrow"
					:class="{
						'is-invalid': errors[clockFieldKey]
					}"
					@change.stop="onChangeDate(clockFieldKey, $event.target.value)"
				/>
				<div class="fst-italic" style="font-size: 0.875em;">
					Значення в сервісному модулі: <span class="fw-bold">
						{{ isNaN(serviceUnitTime) ? ' ' : dateTimeToString(serviceUnitTime, false) }}
					</span>
				</div>
				<div class="invalid-feedback">
					{{ errors[clockFieldKey] }}
				</div>
			</div>
		</div>

		<div class="row form-row gx-2 gb-2 px-2 pb-2 my-1">
			<div class="form-group col-auto ms-auto me-2">
				<button
					type="reset"
					class="btn btn-secondary btn-sm mx-2"
				>
					Відмінити
				</button>
			</div>
			<div class="form-group col-auto me-0">
				<button
					type="submit"
					class="btn btn-primary btn-sm"
					:disabled="!meta.valid"
				>
					<span
						v-if="isPopupOpen"
						class="spinner-border spinner-border-sm"
						role="status"
						aria-hidden="true"
					>
					</span>
					{{ isSetSystemTime ? 'Передати поточний час' : 'Передати' }}
				</button>
			</div>
		</div>

		<div class="col-12 my-0 py-0">
			<div id="valueHelpBlock" class="form-text">
				<p :class="helpPromtClass" class="mb-0">
					{{ helpPromt }}&nbsp;
				</p>
			</div>
		</div>
	</fieldset>
	</Form>
</span>

<!-- Modal -->
<confirmation-popup
	v-model:show="isPopupOpen"
	:confirm-button-title="isLoading || isError || allRpcs.length > 0 ? '' : 'Так'"
	:reject-button-title="isLoading || isError || allRpcs.length > 0 ? 'Закрити' : 'Ні'"
	@confirm="makeRequest()"
>
	<template v-if="station.isActive" v-slot:body>
		<template v-if="isLoading">
			<span
				class="spinner-border spinner-border-sm"
				role="status"
				aria-hidden="true"
			>
			</span>
			Перевірка активних задач телекерування
		</template>
		<template v-else-if="isError">
			Помилка при перевірці активних задач телекерування, закрийте і спробуйте ще.
		</template>
		<template v-else>
			<template v-if="allRpcs.length > 0">
				Станція зараз обробляє інший запит
				("{{ rpcTaskSchema.job.findJob(allRpcs[0][rpcTaskSchema.job.key]).title }}").
				Будь ласка дочекайтесь його виконання.
			</template>
			<template v-else>
				{{ popupPromt }}
			</template>
		</template>
	</template>
	<template v-else v-slot:body>
		Станція не активна. Тільки активні станції виконують запити.
	</template>
</confirmation-popup>
</template>


<script>
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import {
	rpcTaskSchema,
	stationManageJobs,
	useRpcTasks,
} from '@/composables/rpcTasks.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate';
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';
import {
	convertToApiTimeDate,
} from '@/api/api.js'
import {
	dateTimeLocalString,
	dateTimeToString,
} from '@/helpers.js'
import ConfirmationPopup from "@/components/ConfirmationPopup.vue"
import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'


export default {
	requiredPromt: 'Обов\'язкове поле',
	overMinPromt: 'Значення меньше мінімального',
	wrongInputPromt: 'Не відповідає вимогам, допускаються тільки числа більше нуля або нуль',

	CLOCK_KEY: 'clock-timedate',

	components: {
		Form,
        Field,
		ConfirmationPopup,
	},

	props: {
		isEnabled: {
			type: Boolean,
			required: false,
			default: true,
		},
		stationId: {
			type: String,
			required: true,
		},
		inModal: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: {
		// Request creation result
		requestResult: value => typeof value === 'boolean',
	},

	setup() {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const {
			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		} = useRpcTasks()

		return {
			rpcTaskSchema,
			accountStore,
			stationsStore,
			Station,

			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,

			dateTimeToString,
		}
	},

	data() {
		const schema = Yup.object().shape({
			service_unit:  Yup.object().shape({
			}),
		})
		return {
			schema,
			rawValues: {},

			isSetSystemTime: true,

			// Confirmation popup
			isPopupOpen: false,

			// Help promt
			helpPromtClass: '',
			helpPromt: '',
		}
	},

	mounted () {},

	methods: {
		// Delete date field if date is cleared by button "Clear"
		onChangeDate(key, newDate) {
			if (newDate == "") {
				this.$refs.form.setFieldValue(key, null)
				this.isSetSystemTime = true
			} else {
				this.isSetSystemTime = false
			}
		},

		onSubmit (values) {
			if (this.isSetSystemTime) {
				const dt = dateTimeLocalString(new Date())
				this.rawValues = {
					[Station.passportKeys.serviceUnit.self]: {
						[this.$options.CLOCK_KEY]: dt,
					},
				}
			} else {
				this.rawValues = values
			}

			// Begin fetching active tasks
			this.checkActiveTask()
			this.isPopupOpen = true
		},

		checkActiveTask() {
			this.setFilters({
				destinationStationIds: this.stationId,
				isComplete: false,
				jobs: stationManageJobs,
			})
		},

		async makeRequest () {
			if (this.isLoading || this.isError || this.allRpcs.length > 0) {
				return
			}

			const result = await this.writeRpc()
			this.$emit('requestResult', Boolean(result))
		},

		async writeRpc() {
			return await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.setServiceUnitClock.key,
				this.stationId,
				null,
				{
					[rpcTaskSchema.context.targetTime.key]: convertToApiTimeDate(
						this.rawValues[Station.passportKeys.serviceUnit.self][this.$options.CLOCK_KEY]
					),
				}
			)
			.then((result) => {
				if (result) {
					this.printHint('Запит надіслано. Спостерігайте за його станом.')
				} else {
					this.printErr('Сервер відхилив запит постановки завдання.')
				}

				return result
			})
		},

		printHint (msg) {
			this.helpPromtClass = "text-secondary"
			this.helpPromt = msg
		},

		printErr (msg) {
			this.helpPromtClass = "text-danger"
			this.helpPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		clockFieldKey() {
			return `${Station.passportKeys.serviceUnit.self}.${this.$options.CLOCK_KEY}`
		},

		popupPromt() {
			if (this.isSetSystemTime) {
				const timeText = this.targetTime ? ` ${this.targetTime}` : ''
				return `Ви дійсно хочете записати поточний час${timeText} в сервісний модуль?`
			}

			return 'Ви дійсно хочете перезаписати час в сервісному модулі?'
		},

		targetTime() {
			const value = this.rawValues?.[Station.passportKeys.serviceUnit.self]?.[this.$options.CLOCK_KEY]
			if (!value) {
				return ''
			}

			const date = new Date(value)
			if (isNaN(date)) {
				return value
			}

			return dateTimeToString(date, false)
		},

		serviceUnitTime() {
			return new Date(Date.UTC(
				this.station.getMetric(ServiceUnit.metricKeys.clockYear)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockMonth)?.value - 1,
				this.station.getMetric(ServiceUnit.metricKeys.clockDay)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockHour)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockMinute)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockSecond)?.value,
			))
		},

		formValues() {
			// Add clock field
			return {
				[Station.passportKeys.serviceUnit.self]: {
					[this.$options.CLOCK_KEY]: undefined,
				}
			}
		},
	},
}
</script>


<style src="@/assets/stationForm.css" scoped></style>
<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}
</style>